import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useSocket } from '../contexts/SocketContext';
import { useToast } from './Toast';
import { useConfirm } from './Modal';
import { formatBJ } from '../utils/datetime';
import { Package, PackageCheck, ArrowRight, AlertTriangle, Send } from 'lucide-react';

// 证券领取面板：接量页“证券”侧使用；后台“证券代提”也复用（传 targetUser = 代为领取的对象）。
// 版式和排五接量页一致：池中剩余 → 输入数量 → 快捷数量 → 确认提交。
//   规则：一次可以领 N 个包（1 / 3 / 5 …，池里不够就领剩下的）；领了以后要先在工作台点“完成本次”，才能再领。
const MAX_CLAIM = 10;                 // 与服务端 routes/puzzle.js 的 MAX_CLAIM 一致
const QUICK_AMOUNTS = [1, 3, 5];

export default function PuzzleClaimPanel({ targetUser, allowFinish = false, onChanged }) {
  const navigate = useNavigate();
  const { subscribe } = useSocket();
  const { showToast } = useToast();
  const confirm = useConfirm();
  const targetId = targetUser?.id || null;
  const isProxy = !!targetId;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState('');
  const [claiming, setClaiming] = useState(false);
  const [finishing, setFinishing] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get('/puzzle/my-package', { params: targetId ? { userId: targetId } : {} });
      setData(res.data);
    } catch (err) {
      if (targetId) setData(null);
    } finally { setLoading(false); }
  }, [targetId]);

  useEffect(() => { setLoading(true); fetchData(); }, [fetchData]);

  useEffect(() => {
    if (!subscribe) return;
    return subscribe((event) => {
      if (['puzzle_pool_updated', 'puzzle_session_updated', 'puzzle_updated', 'data_refresh'].includes(event)) fetchData();
    });
  }, [subscribe, fetchData]);

  const sessions = data?.sessions || [];
  const pool = data?.pool_count ?? 0;
  const who = isProxy ? (targetUser?.name || '对方') : '您';
  const maxAllowed = Math.min(MAX_CLAIM, pool);

  const handleClaim = async () => {
    const qty = parseInt(quantity, 10);
    if (!qty || qty <= 0) { showToast('请输入有效的领取数量', 'error'); return; }
    if (qty > maxAllowed) { showToast(maxAllowed > 0 ? `本次最多可领 ${maxAllowed} 个包，请调整数量` : '系统暂无可领取的数据包', 'error'); return; }
    setClaiming(true);
    try {
      const res = await api.post('/puzzle/claim', { count: qty, ...(targetId ? { targetUserId: targetId } : {}) });
      showToast(res.data.message || '领取成功', 'success');
      if (res.data.partial) showToast(`池中数据包不足，实际领取 ${res.data.claimed} 个`, 'info');
      setQuantity('');
      await fetchData();
      onChanged && onChanged();
      if (!isProxy) setTimeout(() => navigate('/'), 800);   // 领完直接去工作台看号码
    } catch (err) {
      showToast(err.response?.data?.error || '领取失败', 'error');
      fetchData();
    } finally { setClaiming(false); }
  };

  const handleFinish = async () => {
    const ok = await confirm(`确认替${targetUser?.name || '对方'}完成本次？`, { detail: '完成后对方可以再领取新的数据包，未处理的号码不会再派发。', danger: true });
    if (!ok) return;
    setFinishing(true);
    try {
      await api.post('/puzzle/session/complete', { targetUserId: targetId });
      showToast('已完成本次', 'success');
      await fetchData();
      onChanged && onChanged();
    } catch (err) {
      showToast(err.response?.data?.error || '操作失败', 'error');
    } finally { setFinishing(false); }
  };

  if (loading) return <p style={{ textAlign: 'center', padding: 40, color: 'var(--c-text-secondary)' }}>加载中...</p>;

  return (
    <>
      {/* 池中剩余（和排五“池中可用”一致） */}
      <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ flex: 1, background: 'var(--c-primary-light)', borderRadius: 12, padding: 14, textAlign: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, marginBottom: 6 }}>
            <Package size={14} color="var(--c-primary)" />
            <span style={{ fontSize: 11, color: 'var(--c-text-secondary)', fontWeight: 600 }}>系统剩余可领取</span>
          </div>
          <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--c-primary)' }}>{pool}<span style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-text-secondary)' }}> 个包</span></div>
        </div>
      </div>

      {sessions.length > 0 ? (
        /* 已经领了还没点“完成本次”：和排五“您有未完成的任务”一致，只提示，不重复展示工作台里的内容 */
        <div className="card" style={{ background: 'rgba(255,149,0,0.06)', border: '1px solid rgba(255,149,0,0.2)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertTriangle size={18} color="var(--c-accent)" />
            <div>
              <p style={{ fontWeight: 600, color: '#9a6400', fontSize: 14 }}>{isProxy ? `${who}有未完成的任务` : '您有未完成的任务'}</p>
              <p style={{ fontSize: 13, color: '#9a6400', marginTop: 2, opacity: 0.8 }}>
                {isProxy ? `${who}正在处理 ${sessions.length} 个数据包，完成本次后才能再领取` : `已领取 ${sessions.length} 个数据包，请先在工作台点“完成本次”`}
              </p>
            </div>
          </div>

          {isProxy && (
            <div style={{ marginTop: 10, background: 'var(--c-surface)', borderRadius: 10, padding: '2px 12px' }}>
              {sessions.map((s, i) => (
                <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 0', borderTop: i ? '0.5px solid var(--c-border)' : 'none', fontSize: 13 }}>
                  <PackageCheck size={15} color="var(--c-success)" style={{ flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, wordBreak: 'break-all' }}>{s.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--c-text-secondary)', marginTop: 2 }}>已提交 {s.done} / {s.total} · 领取于 {formatBJ(s.claimed_at)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!isProxy && (
            <button className="btn btn-primary" onClick={() => navigate('/')} style={{ marginTop: 12, height: 44 }}>
              去工作台处理 <ArrowRight size={16} />
            </button>
          )}
          {allowFinish && (
            <button className="btn btn-outline" onClick={handleFinish} disabled={finishing} style={{ marginTop: 10, height: 42 }}>
              {finishing ? '处理中…' : `替${who}完成本次`}
            </button>
          )}
        </div>
      ) : (
        <div className="card">
          <div className="section-title" style={{ paddingTop: 4 }}>{isProxy ? `输入为${who}领取的数量` : '输入领取数量'}</div>

          <input
            className="input"
            type="number"
            min="1"
            max={maxAllowed}
            placeholder={maxAllowed > 0 ? `本次最多 ${maxAllowed} 个包` : '暂无可领取的数据包'}
            value={quantity}
            onChange={e => setQuantity(e.target.value)}
            disabled={maxAllowed <= 0}
            style={{ fontSize: 24, textAlign: 'center', fontWeight: 700, padding: 16, marginTop: 8 }}
          />

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginTop: 14 }}>
            {QUICK_AMOUNTS.map(amt => (
              <button key={amt} className="btn btn-outline btn-sm"
                onClick={() => setQuantity(String(Math.min(amt, maxAllowed)))}
                disabled={maxAllowed <= 0}
                style={{ fontSize: 15, fontWeight: 700, padding: '10px 0', borderRadius: 10 }}>
                {amt}
              </button>
            ))}
          </div>

          <button className="btn btn-primary" onClick={handleClaim}
            disabled={claiming || !quantity || maxAllowed <= 0}
            style={{ marginTop: 14, height: 50, fontSize: 16, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <Send size={18} /> {claiming ? '领取中...' : '确认提交'}
          </button>

          <div style={{ fontSize: 11, color: 'var(--c-text-tertiary)', textAlign: 'center', marginTop: 10, lineHeight: 1.6 }}>
            {pool > 0 ? '数据包先到先得，池里不够时会领剩下的；领取后处理完点“完成本次”才能再领' : '系统暂无可领取的数据包，请等待管理员导入新的数据包'}
          </div>
        </div>
      )}
    </>
  );
}
